export const BILLING_TYPE_COLUMN_LABEL = 'Billing Type'

export const BILLING_TYPE_OPTIONS = ['Fixed', 'Hourly', 'Rate + Hourly'] as const

export const RATE_PLUS_HOURLY_OPTION = BILLING_TYPE_OPTIONS[2]

/** Display names; the stored Billing Type values stay unchanged. */
const BILLING_TYPE_LABELS: Record<string, string> = {
	Fixed: 'Each',
	Hourly: 'Labor Quantity',
	'Rate + Hourly': 'Labor Revenue',
}

export const formatBillingTypeLabel = (value: string | undefined): string => {
	const stored = (value ?? '').trim()
	return BILLING_TYPE_LABELS[stored] ?? stored
}

export const RATE_HOURLY_AMOUNT_KEY = 'hourlyAmount'
export const RATE_HOURLY_PERCENT_KEY = 'ratePercent'
export const RATE_HOURLY_PAY_CODE_KEY = 'payCodeId'

/** Stable EquipmentColumns.Key values for Rate + Hourly factors. Hidden from the grid. */
export const RATE_HOURLY_AMOUNT_COLUMN_KEY = 'hourly_amount'
export const RATE_HOURLY_PERCENT_COLUMN_KEY = 'rate_percent'
export const RATE_HOURLY_PAY_CODE_COLUMN_KEY = 'pay_code_id'

export const isRateHourlyFactorColumnKey = (key: string | undefined): boolean =>
	key === RATE_HOURLY_AMOUNT_COLUMN_KEY ||
	key === RATE_HOURLY_PERCENT_COLUMN_KEY ||
	key === RATE_HOURLY_PAY_CODE_COLUMN_KEY

/** Labor Revenue unit rate from a classification: ST rate × markup %. */
export const calculateLaborRevenueUnitRate = (
	stRate: number | null | undefined,
	markupText: string | undefined,
): number | null => {
	const markup = parseEquipmentAmount(markupText)
	if (stRate == null || markup == null) return null
	const rate = Math.round(stRate * markup * 10) / 1000
	return Number.isFinite(rate) ? rate : null
}

export const isRatePlusHourlyBilling = (value: string | undefined): boolean =>
	(value ?? '').trim() === RATE_PLUS_HOURLY_OPTION

export const parseEquipmentAmount = (value: string | undefined): number | null => {
	const v = (value ?? '').trim().replace(/,/g, '')
	if (!v) return null
	const n = Number(v)
	return Number.isFinite(n) ? n : null
}

export type EquipmentColumnDataType =
	| 'Text'
	| 'Number'
	| 'Currency'
	| 'Date'
	| 'Time'
	| 'BillingType'

/** Column definition for the fleet equipment log grid (default + metadata columns). */
export type EquipmentColumnDef = {
	id: string
	label: string
	isName: boolean
	dataType: EquipmentColumnDataType
	/** Set when the column is backed by EquipmentColumns */
	metaColumnId?: number
}

export const isBillingTypeColumnLabel = (label: string): boolean =>
	label.trim().toLowerCase() === BILLING_TYPE_COLUMN_LABEL.toLowerCase()

/** Maps API columns; Billing Type is inferred from name when stored as Text. */
export const resolveEquipmentColumnDataType = (
	name: string,
	apiDataType: string,
): EquipmentColumnDataType => {
	if (apiDataType === 'Text' && isBillingTypeColumnLabel(name)) return 'BillingType'
	if (
		apiDataType === 'Text' ||
		apiDataType === 'Number' ||
		apiDataType === 'Currency' ||
		apiDataType === 'Date' ||
		apiDataType === 'Time'
	) {
		return apiDataType
	}
	return 'Text'
}

/** BillingType is persisted as Text on the backend. */
export const toApiEquipmentColumnDataType = (dataType: EquipmentColumnDataType): string =>
	dataType === 'BillingType' ? 'Text' : dataType

/** One grid row: string cell values keyed by column id, plus server equipment id. */
export type EquipmentGridRow = Record<string, string> & { __equipmentId: string }


export type EquipmentColumnDto = {
	id: number
	projectId: number
	key: string
	name: string
	dataType: 'Text' | 'Number' | 'Currency' | 'Date' | 'Time' | string
	sortOrder: number
}

export type EquipmentRecordDto = {
	id: number
	name: string
	itemCode?: string | null
	unitPrice?: number | null
	description?: string | null
	onRentDate?: string | null
	offRentDate?: string | null
	values: Record<number, string | null>
	isActive: boolean
	/** True once any time entry line (draft or submitted) uses this charge. */
	isUsed: boolean
}

export type CreateEquipmentColumnRequest = {
	key: string
	name: string
	dataType: string
	sortOrder: number
}

export type CreateEquipmentRequest = {
	name: string
	itemCode?: string | null
	unitPrice?: number | null
	description?: string | null
	onRentDate?: string | null
	offRentDate?: string | null
	values?: Record<number, string | null> | null
}

export type UpdateEquipmentRequest = {
	equipmentId: number
	name: string
	itemCode?: string | null
	unitPrice?: number | null
	description?: string | null
	onRentDate?: string | null
	offRentDate?: string | null
	values?: Record<number, string | null> | null
}